import * as React from 'react';
import { CircleAlert, CircleCheck, Package, TriangleAlert, Truck, UserRoundX } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { OrderKpiMetrics } from '@klareospace/pharos-lib-types';
import { ErrorBanner, KpiCard, KpiCardSkeleton } from '@klareospace/pharos-lib-web-utils';

// ─── Constants ──────────────────────────────────────────────────────────────

const SKELETON_CARD_COUNT = 6;
/** 0-based index of the Completed slot — the only skeleton card whose real counterpart renders a subtitle. */
const COMPLETED_SKELETON_INDEX = 4;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OrderKpisTodayProps {
  isError: boolean;
  isLoading: boolean;
  metrics: OrderKpiMetrics | undefined;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Presentational display of today's `order-kpis` KPI as 6 `KpiCard`s. Data fetching,
 * feature-flag/permission gating, and realtime sync live in `useOrderKpisToday` — see that hook
 * for details.
 */
export function OrderKpisToday({ isError, isLoading, metrics }: OrderKpisTodayProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('kpis');

  if (isError) return <ErrorBanner message={t('loadError')} />;

  if (isLoading || !metrics) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        {Array.from({ length: SKELETON_CARD_COUNT }, (_, index) => (
          <KpiCardSkeleton key={index} withSubtitle={index === COMPLETED_SKELETON_INDEX} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
      <KpiCard
        icon={<Package size={18} />}
        label={t('metrics.total')}
        tooltip={t('tooltips.total')}
        value={metrics.total}
        variant="neutral"
      />
      <KpiCard
        icon={<CircleAlert size={18} />}
        label={t('metrics.anomaly')}
        tooltip={t('tooltips.anomaly')}
        value={metrics.anomaly}
        variant="warning"
      />
      <KpiCard
        icon={<UserRoundX size={18} />}
        label={t('metrics.unassigned')}
        tooltip={t('tooltips.unassigned')}
        value={metrics.unassigned}
        variant="neutral"
      />
      <KpiCard
        icon={<Truck size={18} />}
        label={tCommon('statuses.inProgress')}
        tooltip={t('tooltips.inprogress')}
        value={metrics.inprogress}
        variant="neutral"
      />
      <KpiCard
        icon={<CircleCheck size={18} />}
        label={t('metrics.completed')}
        subtitle={t('completedBreakdown', { late: metrics.completedLate, onTime: metrics.completedOnTime })}
        tooltip={t('tooltips.completed')}
        value={metrics.completed}
        valueAccent="success"
        variant="success"
      />
      <KpiCard
        icon={<TriangleAlert size={18} />}
        label={t('common.failed')}
        tooltip={t('tooltips.failed')}
        value={metrics.failed}
        valueAccent="destructive"
        variant="danger"
      />
    </div>
  );
}
